import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { expandHalfHourStarts } from '../lib/slots';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { Calendar } from '../components/ui/calendar';
import { es as esLocale, enGB as enLocale, de as deLocale } from 'date-fns/locale';
import { Clock, Check, Calendar as CalendarIcon, User, Loader2, Sparkles, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { getPlans } from '../config/classPlans';
import { trackTrialCtaClick, trackBookingStart, trackBookingLead } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRIAL_ONCE_NOTE = {
  es: 'La clase de prueba está disponible una vez por alumno. Si ya la has realizado, puedes elegir una clase individual o un pack.',
  en: 'One trial class per student. If you have already had yours, choose an individual class or a pack.',
  de: 'Nur eine Probestunde pro Lernenden. Wenn du sie schon hattest, wähle eine Einzelstunde oder ein Paket.',
};

const PAID_HEADER = {
  es: { title: 'Reserva tu clase', lead: 'Selecciona la opción que mejor se adapte a ti y elige una fecha disponible.' },
  en: { title: 'Book your class', lead: 'Choose the option that suits you best and pick an available date.' },
  de: { title: 'Buche deine Stunde', lead: 'Wähle die passende Option und ein freies Datum.' },
};

const PACK_NOTE = {
  es: 'Reserva aquí la fecha de tu primera clase; el resto las coordinamos juntas por email.',
  en: 'Book the date of your first class here; we will arrange the rest together by email.',
  de: 'Buche hier den Termin deiner ersten Stunde; die übrigen stimmen wir per E-Mail ab.',
};

const UI = {
  es: {
    choose: 'Elegir y ver fechas', chosen: 'Has elegido', change: 'Cambiar tipo de clase',
    popular: 'MÁS POPULAR', perClass: (p) => `${p}/clase`, save: (s) => `Ahorras ${s}`,
    startHere: 'EMPIEZA AQUÍ', chooseTrial: 'Reservar mi clase de prueba gratis',
  },
  en: {
    choose: 'Choose and see dates', chosen: 'You chose', change: 'Change class type',
    popular: 'MOST POPULAR', perClass: (p) => `${p}/class`, save: (s) => `Save ${s}`,
    startHere: 'START HERE', chooseTrial: 'Book my free trial lesson',
  },
  de: {
    choose: 'Auswählen und Termine sehen', chosen: 'Deine Auswahl', change: 'Kursart ändern',
    popular: 'AM BELIEBTESTEN', perClass: (p) => `${p}/Stunde`, save: (s) => `Spare ${s}`,
    startHere: 'HIER STARTEN', chooseTrial: 'Kostenlose Probestunde buchen',
  },
};

const CTA_BY_TYPE = {
  es: { prueba: 'Reservar mi clase gratis', individual: 'Reservar mi clase', pack5: 'Reservar mi pack', pack10: 'Reservar mi pack', dele: 'Reservar mi clase DELE', dele_pack5: 'Reservar mi bono DELE' },
  en: { prueba: 'Book my free class', individual: 'Book my class', pack5: 'Book my pack', pack10: 'Book my pack', dele: 'Book my DELE class', dele_pack5: 'Book my DELE pack' },
  de: { prueba: 'Kostenlose Stunde buchen', individual: 'Meine Stunde buchen', pack5: 'Mein Paket buchen', pack10: 'Mein Paket buchen', dele: 'DELE-Stunde buchen', dele_pack5: 'DELE-Paket buchen' },
};

const PACK_SIZES = { pack5: 5, pack10: 10, dele_pack5: 5 };

const PACK_UI = {
  es: {
    pick: (n, total) => `Elige las fechas de tus clases (${n}/${total})`,
    chosenList: 'Tus clases elegidas',
    remove: 'Quitar',
    hint: (left) => `Puedes elegir hasta ${left} fecha${left === 1 ? '' : 's'} más ahora, o reservarlas después desde tu enlace privado.`,
    full: 'Ya has elegido todas las clases de tu pack.',
    confirm: (n) => `Reservar mi pack (${n} clase${n === 1 ? '' : 's'})`,
    atLeastOne: 'Elige al menos una fecha para tu primera clase.',
  },
  en: {
    pick: (n, total) => `Choose the dates for your classes (${n}/${total})`,
    chosenList: 'Your chosen classes',
    remove: 'Remove',
    hint: (left) => `You can pick up to ${left} more date${left === 1 ? '' : 's'} now, or book them later from your private link.`,
    full: 'You have already chosen all the classes in your pack.',
    confirm: (n) => `Book my pack (${n} class${n === 1 ? '' : 'es'})`,
    atLeastOne: 'Choose at least one date for your first class.',
  },
  de: {
    pick: (n, total) => `Wähle die Termine für deine Stunden (${n}/${total})`,
    chosenList: 'Deine gewählten Stunden',
    remove: 'Entfernen',
    hint: (left) => `Du kannst jetzt bis zu ${left} weitere Termine wählen oder sie später über deinen privaten Link buchen.`,
    full: 'Du hast schon alle Stunden deines Pakets gewählt.',
    confirm: (n) => `Mein Paket buchen (${n} Stunde${n === 1 ? '' : 'n'})`,
    atLeastOne: 'Wähle mindestens einen Termin für deine erste Stunde.',
  },
};

export const Booking = () => {
  const { t, language } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const plans = getPlans(language);
  const ui = UI[language] || UI.es;

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [availability, setAvailability] = useState(null);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  // Encabezado alternativo cuando el alumno llega desde una opción de pago
  const paidHeader = selectedPlanId && selectedPlanId !== 'prueba'
    ? (PAID_HEADER[language] || PAID_HEADER.es)
    : null;
  const packTotal = PACK_SIZES[selectedPlanId] || 0;
  const isPack = packTotal > 0;
  const packUi = PACK_UI[language] || PACK_UI.es;
  const [packSessions, setPackSessions] = useState([]);

  const toggleSession = (date, time) => {
    const key = `${date} ${time}`;
    setPackSessions((prev) => {
      const exists = prev.find((s) => `${s.date} ${s.time}` === key);
      if (exists) return prev.filter((s) => `${s.date} ${s.time}` !== key);
      if (prev.length >= packTotal) return prev;
      return [...prev, { date, time }].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
    });
  };

  const fetchAvailability = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/availability`);
      setAvailability(data);
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  }, []);

  // Always work with fresh availability: on mount, when the tab regains focus
  // and every time the student picks a date (another student may have just booked).
  useEffect(() => {
    fetchAvailability();
    const onFocus = () => {
      if (document.visibilityState !== 'hidden') fetchAvailability();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchAvailability]);

  useEffect(() => {
    if (selectedDate) fetchAvailability();
  }, [selectedDate, fetchAvailability]);

  useEffect(() => {
    const onSelectPlan = (e) => {
      const id = e.detail;
      if (!id) return;
      setSelectedPlanId(id);
      setSelectedDate(null);
      setSelectedTime(null);
      setPackSessions([]);
      setError('');
      setTimeout(() => {
        document.getElementById('booking-calendar')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      trackBookingStart(id, 'pricing');
    };
    window.addEventListener('select-class-plan', onSelectPlan);
    return () => window.removeEventListener('select-class-plan', onSelectPlan);
  }, []);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const availableTimes = React.useMemo(() => {
    if (!selectedDate || !availability) return [];
    const dayKey = String(selectedDate.getDay());
    const dayConfig = availability.weekly?.[dayKey];
    const dateStr = formatDate(selectedDate);
    const opened = availability.opened_slots?.[dateStr] || [];
    const taken = new Set([
      ...(availability.booked_slots?.[dateStr] || []),
      ...(availability.blocked_slots?.[dateStr] || []),
    ]);
    const baseSlots = dayConfig && dayConfig.enabled ? (dayConfig.slots || []) : [];
    const merged = expandHalfHourStarts([...baseSlots, ...opened]);
    return merged.filter((s) => !taken.has(s));
  }, [selectedDate, availability]);

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return true;
    const dateStr = formatDate(date);
    if ((availability.blocked_dates || []).includes(dateStr)) return true;
    const dayKey = String(date.getDay());
    const dayConfig = availability.weekly?.[dayKey];
    const weeklyEnabled = dayConfig && dayConfig.enabled && (dayConfig.slots || []).length > 0;
    const hasOpenedSlots = (availability.opened_slots?.[dateStr] || []).length > 0;
    if (!weeklyEnabled && !hasOpenedSlots) return true;
    return false;
  };

  const choosePlan = (id) => {
    setSelectedPlanId(id);
    setError('');
    if (id === 'prueba') trackTrialCtaClick('trial_section');
    trackBookingStart(id, 'trial_section');
    setTimeout(() => {
      document.getElementById('booking-calendar')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  };

  const resetPlan = () => {
    setSelectedPlanId(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setPackSessions([]);
    setError('');
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const isFormValid = () => {
    const basics = selectedPlanId && name.trim() && email.trim() && /\S+@\S+\.\S+/.test(email);
    if (isPack) return basics && packSessions.length > 0;
    return basics && selectedDate && selectedTime;
  };

  const handleConfirmBooking = async () => {
    setError('');
    if (isPack && packSessions.length === 0) {
      setError(packUi.atLeastOne);
      return;
    }
    if (!isFormValid()) {
      setError(t.booking.errorAllFields);
      return;
    }

    setIsSubmitting(true);
    try {
      if (isPack) {
        await axios.post(`${API}/packs`, {
          name: name.trim(),
          email: email.trim(),
          pack_type: selectedPlanId,
          sessions: packSessions,
          language: language,
          message: message.trim() || null,
        });
      } else {
        await axios.post(`${API}/bookings`, {
          name: name.trim(),
          email: email.trim(),
          class_type: selectedPlanId,
          date: formatDate(selectedDate),
          time: selectedTime,
          language: language,
          message: message.trim() || null,
        });
      }

      setShowSuccess(true);
      trackBookingLead(selectedPlanId);
      await fetchAvailability();
      setTimeout(() => {
        setShowSuccess(false);
        setSelectedPlanId(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setPackSessions([]);
        setName('');
        setEmail('');
        setMessage('');
      }, 5000);
    } catch (err) {
      console.error('Error creating booking:', err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setError(detail || t.booking.errorSlotTaken);
        setSelectedTime(null);
        setPackSessions([]);
        await fetchAvailability();
      } else if (detail) {
        setError(detail);
      } else {
        setError(t.booking.errorProcessing);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-white via-[#E8F5FA]/50 to-white relative overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.booking.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6" data-testid="booking-title">
            {paidHeader ? paidHeader.title : t.booking.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
          {paidHeader ? (
            <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl mx-auto" data-testid="booking-lead">
              {paidHeader.lead}
            </p>
          ) : (
            <>
          {t.booking.lead && (
            <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl mx-auto" data-testid="booking-lead">
              {t.booking.lead}
            </p>
          )}
          {t.booking.intro && (
            <p className="mt-4 text-base text-gray-600 max-w-2xl mx-auto" data-testid="booking-intro">
              {t.booking.intro}
            </p>
          )}
          {t.booking.note && (
            <p className="mt-5 inline-block px-5 py-2.5 rounded-full bg-[#FFF0EF] text-[#E38987] text-sm font-semibold" data-testid="booking-note">
              {t.booking.note}
            </p>
          )}
            </>
          )}
        </motion.div>

        {/* STEP 1 — choose the class type (no calendar yet) */}
        {!selectedPlan && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            data-testid="class-type-grid"
          >
            <h3 className="text-base md:text-lg font-bold text-[#4A4A4A] text-center mb-10">
              {t.booking.selectClassType}
            </h3>
            <div className="max-w-2xl mx-auto">
              {plans.filter((p) => p.id === 'prueba').map((plan, index) => (
                <motion.button
                  type="button"
                  key={plan.id}
                  onClick={() => choosePlan(plan.id)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.07 }}
                  whileHover={{ y: -6 }}
                  data-testid={`class-type-${plan.id}`}
                  className={`relative text-left p-6 rounded-2xl border-2 bg-white transition-all duration-300 hover:shadow-2xl flex flex-col ${
                    plan.id === 'prueba'
                      ? 'w-full border-[#F5A6A4] shadow-2xl ring-4 ring-[#F5A6A4]/20'
                      : plan.featured ? 'border-[#A8D8EA] shadow-xl' : 'border-gray-200 shadow-lg hover:border-[#A8D8EA]'
                  }`}
                >
                  {plan.id === 'prueba' && (
                    <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-[#F5A6A4] to-[#E38987] text-white text-[10px] font-bold tracking-wider flex items-center gap-1" data-testid="trial-start-here-badge">
                      <Star className="w-3 h-3 fill-white" />
                      {ui.startHere}
                    </span>
                  )}
                  {plan.featured && (
                    <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white text-[10px] font-bold tracking-wider flex items-center gap-1">
                      <Star className="w-3 h-3 fill-white" />
                      {ui.popular}
                    </span>
                  )}
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div>
                      <h4 className="text-xl font-bold text-[#4A4A4A] mb-1">{plan.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {plan.duration}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#F5A6A4] whitespace-nowrap">{plan.price}</div>
                      {plan.perClassPrice && (
                        <div className="text-xs text-gray-500">{ui.perClass(plan.perClassPrice)}</div>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

                  <ul className="space-y-1.5 mb-5">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-[#7DBDD9] flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {plan.id === 'prueba' && (
                    <p data-testid="trial-once-note" className="text-xs font-semibold text-[#E38987] mb-4">
                      {TRIAL_ONCE_NOTE[language] || TRIAL_ONCE_NOTE.es}
                    </p>
                  )}

                  <span className="mt-auto w-full py-3 rounded-xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-md">
                    {plan.id === 'prueba' ? ui.chooseTrial : ui.choose}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* STEP 2 — calendar, time and contact details for the chosen class */}
        {selectedPlan && (
          <div className="grid lg:grid-cols-2 gap-12" data-testid="booking-step-calendar">
            {/* Chosen plan summary */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
            >
              <button
                type="button"
                onClick={resetPlan}
                data-testid="change-class-type-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#7DBDD9] hover:text-[#5DA8CC]"
              >
                <ArrowLeft className="w-4 h-4" />
                {ui.change}
              </button>

              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow-xl" data-testid="selected-plan-card">
                <p className="text-xs font-bold uppercase tracking-wider text-white/80 mb-2">{ui.chosen}</p>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <h4 className="text-xl font-bold mb-1">{selectedPlan.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-white/90">
                      <Clock className="w-4 h-4" />
                      {selectedPlan.duration}
                    </div>
                  </div>
                  <div className="text-2xl font-bold whitespace-nowrap">{selectedPlan.price}</div>
                </div>
                <p className="text-sm text-white/90 mt-3">{selectedPlan.description}</p>
                {(selectedPlan.id === 'pack5' || selectedPlan.id === 'pack10') && (
                  <p className="mt-3 text-xs font-semibold text-white/90" data-testid="pack-note">
                    {PACK_NOTE[language] || PACK_NOTE.es}
                  </p>
                )}
                {selectedPlan.id === 'prueba' && (
                  <p className="mt-3 text-xs font-semibold text-white/90">
                    {TRIAL_ONCE_NOTE[language] || TRIAL_ONCE_NOTE.es}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Calendar & time & form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-6"
            >
              <div id="booking-calendar" className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center space-x-2 mb-4">
                  <CalendarIcon className="w-5 h-5 text-[#A8D8EA]" />
                  <h3 className="text-lg font-bold text-[#4A4A4A]">{t.booking.selectDate}</h3>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => { setSelectedDate(date); setSelectedTime(null); }}
                  disabled={isDateDisabled}
                  locale={language === 'de' ? deLocale : language === 'en' ? enLocale : esLocale}
                  className="rounded-md border-0"
                />
              </div>

              {selectedDate && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
                >
                  <div className="flex items-center space-x-2 mb-4">
                    <Clock className="w-5 h-5 text-[#A8D8EA]" />
                    <h3 className="text-lg font-bold text-[#4A4A4A]">
                      {isPack ? packUi.pick(packSessions.length, packTotal) : t.booking.selectTime}
                    </h3>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {availableTimes.map((time) => {
                      const dateStr = formatDate(selectedDate);
                      const picked = isPack
                        ? packSessions.some((s) => s.date === dateStr && s.time === time)
                        : selectedTime === time;
                      return (
                        <motion.button
                          key={time}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => (isPack ? toggleSession(dateStr, time) : setSelectedTime(time))}
                          data-testid={`time-slot-${time}`}
                          className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                            picked
                              ? 'bg-[#A8D8EA] text-white shadow-md'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {time}
                        </motion.button>
                      );
                    })}
                  </div>
                  {availableTimes.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-2" data-testid="no-slots-message">
                      {t.booking.noSlots}
                    </p>
                  )}
                  {isPack && (
                    <p className="mt-4 text-xs text-gray-500">
                      {packSessions.length >= packTotal
                        ? packUi.full
                        : packUi.hint(packTotal - packSessions.length)}
                    </p>
                  )}
                </motion.div>
              )}

              {/* Pack: list of chosen sessions */}
              {isPack && packSessions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
                  data-testid="pack-sessions-list"
                >
                  <h3 className="text-lg font-bold text-[#4A4A4A] mb-4">
                    {packUi.chosenList} ({packSessions.length}/{packTotal})
                  </h3>
                  <ul className="space-y-2">
                    {packSessions.map((s, i) => (
                      <li
                        key={`${s.date}-${s.time}`}
                        data-testid={`pack-session-${i}`}
                        className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-[#E8F5FA]"
                      >
                        <span className="text-sm font-semibold text-[#4A4A4A]">
                          {s.date} · {s.time}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleSession(s.date, s.time)}
                          data-testid={`pack-session-remove-${i}`}
                          className="text-xs font-semibold text-[#E38987] hover:underline"
                        >
                          {packUi.remove}
                        </button>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {((isPack && packSessions.length > 0) || (!isPack && selectedDate && selectedTime)) && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 space-y-4"
                  data-testid="booking-form-fields"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="w-5 h-5 text-[#A8D8EA]" />
                    <h3 className="text-lg font-bold text-[#4A4A4A]">{t.booking.yourData}</h3>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.booking.nameLabel} *</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="booking-name-input"
                      placeholder={t.booking.namePlaceholder}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.booking.emailLabel} *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="booking-email-input"
                      placeholder={t.booking.emailPlaceholder}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.booking.messageLabel}</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      data-testid="booking-message-input"
                      placeholder={t.booking.messagePlaceholder}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none transition-all resize-none"
                    />
                  </div>
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
                  data-testid="booking-error"
                >
                  {error}
                </motion.div>
              )}

              <motion.button
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                onClick={handleConfirmBooking}
                disabled={isSubmitting}
                data-testid="confirm-booking-btn"
                className="w-full py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t.booking.processing}</span>
                  </>
                ) : (
                  <>
                    {selectedPlanId === 'prueba' && <Sparkles className="w-5 h-5" />}
                    <span>
                      {isPack
                        ? packUi.confirm(packSessions.length)
                        : (CTA_BY_TYPE[language] || CTA_BY_TYPE.es)[selectedPlanId] || t.booking.confirm}
                    </span>
                  </>
                )}
              </motion.button>
            </motion.div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4"
            data-testid="booking-success-modal"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#4A4A4A] mb-2">{t.booking.successTitle}</h3>
                {isPack ? (
                  <p className="text-gray-600 mb-4" data-testid="pack-success-text">
                    {packSessions.length}/{packTotal} {language === 'de' ? 'Stunden gebucht' : language === 'en' ? 'classes booked' : 'clases reservadas'}
                  </p>
                ) : (
                  <p className="text-gray-600 mb-4">
                    {t.booking.successMessage} {selectedDate?.toLocaleDateString(language === 'de' ? 'de-DE' : language === 'en' ? 'en-GB' : 'es-ES')} {t.booking.successAt} {selectedTime}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  {t.booking.successContact} ({email}) {t.booking.successContactSuffix}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
